import React, { useState, useEffect } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  LabelList,
  ResponsiveContainer,
  Cell,
} from "recharts";
import "../../assets/games.css";
import Loader from "../Utils/Loader";
import { colors } from "../../assets/theme_setup";

// Custom empty tooltip (to disable the tooltip)
const CustomTooltip = () => null;

const TeamComparisonChart = ({ comparison, loading }) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 960);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 960);
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  if (loading) {
    return <Loader />;
  }

  if (!comparison || !comparison.home || !comparison.away) {
    return <div className="game-comp-error">No comparison data available</div>;
  }

  // Format function to round to 1 decimal and remove .0 if it's a whole number
  const formatValue = (value) => {
    const rounded = Math.round(value * 10) / 10;
    return rounded % 1 === 0 ? Math.floor(rounded) : rounded;
  };

  // Define metrics and their labels
  const metrics = [
    { key: "points", label: "PTS" },
    { key: "fgPct", label: "FG%" },
    { key: "threePct", label: "3PT%" },
    { key: "ftPct", label: "FT%" },
    { key: "assists", label: "AST" },
    { key: "rebounds", label: "REB" },
    { key: "fouls", label: "PF" },
    { key: "turnovers", label: "TO" },
    { key: "blocks", label: "BLK" },
    { key: "steals", label: "STL" },
  ];

  // Create unified data for more interactive visualization
  const comparisonData = metrics.map((metric) => {
    const homeValue = formatValue(comparison.home[metric.key]);
    const awayValue = formatValue(comparison.away[metric.key]);
    const difference = homeValue - awayValue;

    return {
      metric: metric.label,
      home: -homeValue,
      away: awayValue,
      homeActual: homeValue,
      awayActual: awayValue,
      homeAdvantage: difference > 0,
      difference: Math.abs(difference),
    };
  });

  // Add team colors
  const homeColor = "#1D428A"; // Use theme colors here
  const awayColor = "#BF2C36";
  const neutralColor = "#555555";

  return (
    <div className="game-comp-container">
      <div className="game-comp-team-headers">
        <div className="game-comp-team-header home">
          <div
            className="game-comp-team-badge"
            style={{ backgroundColor: homeColor }}
          ></div>
          <h3>{comparison.home.teamName}</h3>
        </div>
        <div className="game-comp-team-header away">
          <div
            className="game-comp-team-badge"
            style={{ backgroundColor: awayColor }}
          ></div>
          <h3>{comparison.away.teamName}</h3>
        </div>
      </div>

      <div className="game-comp-chart-container">
        {/* Left/Home Team Chart */}
        <div className="game-comp-chart left">
          <ResponsiveContainer width="100%" height={600}>
            <BarChart
              data={comparisonData}
              layout="vertical"
              margin={{ top: 20, right: 0, left: 30, bottom: 20 }}
              barSize={30}
            >
              <CartesianGrid strokeDasharray="3 3" horizontal={false} />
              <XAxis
                type="number"
                domain={[-100, 0]}
                tickFormatter={(value) => Math.abs(value)}
                tick={{ fill: colors.text.secondary }}
              />
              <YAxis
                dataKey="metric"
                type="category"
                axisLine={false}
                tickLine={false}
                tick={false}
                width={0}
              />

              {/* Home Team Bar */}
              <Bar dataKey="home" fill={homeColor}>
                <LabelList
                  dataKey="homeActual"
                  position="insideRight"
                  fill="#FFFFFF"
                  style={{ fontWeight: "bold", fontSize: "14px" }}
                />
                {comparisonData.map((entry, index) => (
                  <Cell
                    key={`home-${index}`}
                    fill={entry.homeAdvantage ? homeColor : neutralColor}
                    opacity={entry.homeAdvantage ? 1 : 0.7}
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Center Labels */}
        <div className="game-comp-labels">
          {comparisonData.map((data, index) => (
            <div key={index} className="game-comp-metric-label">
              {data.metric}
            </div>
          ))}
        </div>

        {/* Right/Away Team Chart */}
        <div className="game-comp-chart right">
          <ResponsiveContainer width="100%" height={600}>
            <BarChart
              data={comparisonData}
              layout="vertical"
              margin={{ top: 20, right: 30, left: 0, bottom: 20 }}
              barSize={30}
            >
              <CartesianGrid strokeDasharray="3 3" horizontal={false} />
              <XAxis
                type="number"
                domain={[0, 100]}
                tick={{ fill: colors.text.secondary }}
              />
              <YAxis
                dataKey="metric"
                type="category"
                axisLine={false}
                tickLine={false}
                tick={false}
                width={0}
                orientation="right"
              />

              {/* Away Team Bar */}
              <Bar dataKey="away" fill={awayColor}>
                <LabelList
                  dataKey="awayActual"
                  position="insideLeft"
                  fill="#FFFFFF"
                  style={{ fontWeight: "bold", fontSize: "14px" }}
                />
                {comparisonData.map((entry, index) => (
                  <Cell
                    key={`away-${index}`}
                    fill={!entry.homeAdvantage ? awayColor : neutralColor}
                    opacity={!entry.homeAdvantage ? 1 : 0.7}
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Mobile View */}
        <div className="game-comp-mobile-view">
          {comparisonData.map((data, index) => (
            <div key={index} className="game-comp-metric-row">
              <div
                className={`game-comp-home-value ${
                  data.homeAdvantage ? "home-winner" : ""
                }`}
                style={{ position: "relative" }}
              >
                {data.homeActual}
              </div>
              <div className="game-comp-metric-label">{data.metric}</div>
              <div
                className={`game-comp-away-value ${
                  !data.homeAdvantage ? "away-winner" : ""
                }`}
                style={{ position: "relative" }}
              >
                {data.awayActual}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default TeamComparisonChart;
